/**
 * OpenAI Direct Provider
 */

import { DirectProvider } from '../base/DirectProvider'
import type { AIResponseWithReason, ContinueContext } from '../../types'
import { getContinuePrompt } from '../../writing-utils/writing-prompts'

export class OpenAIProvider extends DirectProvider {
  name = 'OpenAI'
  
  constructor(apiKey: string, model = 'gpt-4o-mini') {
    // Determine max OUTPUT tokens based on model (not context window)
    let maxTokens = 4096  // Default output limit
    
    // GPT-4o models
    if (model.includes('gpt-4o-2024-08-06')) {
      maxTokens = 16384  // GPT-4o August 2024 supports 16K output
    } else if (model.includes('gpt-4o-mini')) {
      maxTokens = 16384  // GPT-4o mini also supports 16K output
    } else if (model.includes('gpt-4o')) {
      maxTokens = 4096  // Standard GPT-4o has 4K output limit
    }
    // GPT-4 Turbo
    else if (model.includes('gpt-4-turbo') || model.includes('gpt-4-1106')) {
      maxTokens = 4096  // GPT-4 Turbo has 4K output limit despite 128K context
    }
    // Other GPT-4 models
    else if (model.includes('gpt-4-32k')) {
      maxTokens = 4096  // GPT-4 32K still has 4K output limit
    } else if (model.includes('gpt-4')) {
      maxTokens = 4096  // Standard GPT-4 has 4K output limit
    }
    // GPT-3.5 models
    else if (model.includes('gpt-3.5-turbo-16k')) {
      maxTokens = 4096  // GPT-3.5 16K has 4K output limit
    } else if (model.includes('gpt-3.5')) {
      maxTokens = 4096  // Standard GPT-3.5 has 4K output limit
    }
    
    super({
      apiKey,
      model,
      apiUrl: 'https://api.openai.com/v1/chat/completions',
      maxTokens
    })
  }
  
  protected buildHeaders(): HeadersInit {
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${this.apiKey}`
    }
  }
  
  protected buildRequestBody(systemPrompt: string, userPrompt: string): any {
    return {
      model: this.model,
      messages: [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: userPrompt }
      ],
      temperature: this.temperature,
      max_tokens: this.maxTokens
    }
  }
  
  protected extractTextFromResponse(data: any): string {
    return data.choices[0].message.content
  }

  /**
   * Continue writing with streaming support
   */
  async continueStreaming(
    text: string, 
    context?: ContinueContext,
    onChunk?: (chunk: string) => void
  ): Promise<string | AIResponseWithReason> {
    const mode = context?.mode || 'sentence'
    const prompts = getContinuePrompt(mode, context)
    
    // Modify system prompt for streaming - request plain text only
    const streamingSystemPrompt = prompts.system.replace(
      /응답 형식:[\s\S]*$/m,
      '이어쓴 텍스트만 반환하세요. JSON 형식이나 추가 설명 없이 순수한 텍스트만 반환하세요.'
    )
    
    const requestBody = {
      model: this.model,
      messages: [
        { role: 'system', content: streamingSystemPrompt },
        { role: 'user', content: prompts.user(text) }
      ],
      temperature: this.temperature,
      max_tokens: this.maxTokens,
      stream: true  // Enable streaming
    }

    const response = await fetch(this.apiUrl, {
      method: 'POST',
      headers: this.buildHeaders(),
      body: JSON.stringify(requestBody)
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`OpenAI API error: ${response.status} - ${errorText}`)
    }

    const reader = response.body?.getReader()
    if (!reader) {
      throw new Error('Failed to get response reader')
    }

    const decoder = new TextDecoder()
    let fullText = ''
    let isCollectingJSON = false
    let jsonBuffer = ''

    try {
      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        const chunk = decoder.decode(value)
        const lines = chunk.split('\n').filter(line => line.trim() !== '')

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6)
            if (data === '[DONE]') {
              break
            }
            
            try {
              const parsed = JSON.parse(data)
              const content = parsed.choices?.[0]?.delta?.content
              if (content) {
                // Check if the content starts a JSON response
                if (!isCollectingJSON && content.includes('{"continuation"')) {
                  isCollectingJSON = true
                  jsonBuffer = content
                } else if (isCollectingJSON) {
                  jsonBuffer += content
                  // Try to parse complete JSON
                  if (jsonBuffer.includes('}')) {
                    try {
                      const jsonMatch = jsonBuffer.match(/\{"continuation":\s*"([^"]*)"\}/)
                      if (jsonMatch) {
                        const extractedText = jsonMatch[1]
                        fullText = extractedText
                        onChunk?.(extractedText)
                        isCollectingJSON = false
                      }
                    } catch {
                      // Continue collecting
                    }
                  }
                } else {
                  // Plain text streaming
                  fullText += content
                  onChunk?.(content)
                }
              }
            } catch (e) {
              // Skip invalid JSON
            }
          }
        }
      }
    } finally {
      reader.releaseLock()
    }

    // Final cleanup if we collected JSON
    if (isCollectingJSON && jsonBuffer) {
      try {
        const jsonMatch = jsonBuffer.match(/\{"continuation":\s*"([^"]*)"\}/)
        if (jsonMatch) {
          fullText = jsonMatch[1]
        }
      } catch {}
    }

    return {
      text: fullText,
      reason: undefined  // Continue is generation, not modification
    }
  }
  
  /**
   * Process custom prompt with streaming support
   */
  async processCustomPromptStreaming(
    text: string, 
    userPrompt: string, 
    mode?: 'sentence' | 'paragraph',
    onChunk?: (chunk: string) => void
  ): Promise<AIResponseWithReason> {
    // Check if this is a generation request (empty text) or modification request
    const isGeneration = !text || text.trim() === ''
    
    let systemPrompt: string
    let fullUserPrompt: string
    
    if (isGeneration) {
      // Text generation mode
      systemPrompt = `한국어 텍스트를 생성하는 전문 작가입니다.`
      
      if (mode === 'sentence') {
        systemPrompt += ' 한 문장으로 간결하게 작성하세요.'
      } else if (mode === 'paragraph') {
        systemPrompt += ' 3-5 문장으로 구성된 한 문단으로 작성하세요.'
      }
      
      systemPrompt += ' 생성된 텍스트만 반환하세요. JSON 형식이나 추가 설명 없이 순수한 텍스트만 반환하세요.'
      fullUserPrompt = `요청사항: ${userPrompt}`
    } else {
      // Text modification mode
      systemPrompt = `다음 텍스트를 사용자의 요청에 따라 수정해주세요.`
      
      if (mode === 'sentence') {
        systemPrompt += ' 한 문장으로 간결하게 작성하세요.'
      } else if (mode === 'paragraph') {
        systemPrompt += ' 한 문단으로 자세하게 작성하세요.'
      }
      
      systemPrompt += ' 수정된 텍스트만 반환하세요. JSON 형식이나 추가 설명 없이 순수한 텍스트만 반환하세요.'
      fullUserPrompt = `원문: ${text}\n\n요청사항: ${userPrompt}`
    }
    
    const requestBody = {
      model: this.model,
      messages: [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: fullUserPrompt }
      ],
      temperature: this.temperature,
      max_tokens: this.maxTokens,
      stream: true  // Enable streaming
    }

    const response = await fetch(this.apiUrl, {
      method: 'POST',
      headers: this.buildHeaders(),
      body: JSON.stringify(requestBody)
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`OpenAI API error: ${response.status} - ${errorText}`)
    }

    const reader = response.body?.getReader()
    if (!reader) {
      throw new Error('Failed to get response reader')
    }

    const decoder = new TextDecoder()
    let fullText = ''

    try {
      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        const chunk = decoder.decode(value)
        const lines = chunk.split('\n').filter(line => line.trim() !== '')

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6)
            if (data === '[DONE]') {
              break
            }
            
            try {
              const parsed = JSON.parse(data)
              const content = parsed.choices?.[0]?.delta?.content
              if (content) {
                fullText += content
                onChunk?.(content)
              }
            } catch (e) {
              // Skip invalid JSON
            }
          }
        }
      }
    } finally {
      reader.releaseLock()
    }

    // Only provide reason for modifications, not for generations
    if (isGeneration) {
      return {
        text: fullText,
        reason: undefined
      }
    }

    return {
      text: fullText,
      reason: `프롬프트: "${userPrompt}"`
    }
  }
}